import * as angular from 'angular';
import { parseDomain } from 'helpers/parseDomain';

import {
  Input,
  Inject,
  OnInit,
  Component,
} from 'ng-metadata/core';

import { CheckoutService } from '../checkout.service';

import { AuthService } from 'services/auth.service';
import { CartService } from 'services/cart.service';
import { OrderService } from 'services/order.service';
import { DialogService } from 'services/dialog.service';
import { BugsnagService } from 'services/bugsnag.service';
import { SessionService } from 'services/session.service';
import { AuthNetService } from 'services/auth-net.service';
import { EcommerceService } from 'services/ecommerce.service';
import { EtomsService } from 'services/etoms.service';

import submitHtml from './submit.html';
import './submit.scss';

@Component({
  selector: 'gw-portal-checkout-submit',
  template: submitHtml,
})
export class CheckoutSubmitComponent implements OnInit {
  public GW = (<any>window).GW;
  public fbq = (<any>window).fbq;
  public laroute = (<any>window).laroute;

  public form;
  public cart = this.GW.cart;
  public user = this.GW.user;
  public order = this.orderService.order;
  public creditCard: any = {};
  public authNetErrors : any = {
    hasError : false
  };

  public state = {
    portal: '',
    portalNiceName: '',
    terms: false,
    mustAgree: false,
    submitting: false,
    returning: null,
  };

  constructor(
    @Inject('$log') private $log,
    @Inject('$filter') private $filter,
    @Inject(AuthService) private authService: AuthService,
    @Inject(CartService) private cartService: CartService,
    @Inject(OrderService) private orderService: OrderService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(AuthNetService) private authNetService: AuthNetService,
    @Inject(BugsnagService) private bugsnagService: BugsnagService,
    @Inject(SessionService) private sessionService: SessionService,
    @Inject(CheckoutService) private checkoutService: CheckoutService,
    @Inject(EcommerceService) private ecommerceService: EcommerceService,
    @Inject(EtomsService) private etomsService: EtomsService,
  ) {}

  /**
   * Component init hook
   */
  ngOnInit() {
    // Make sure they complete account step first
    if (!this.user && this.order.account.email === '') {
      return window.location.assign(this.laroute.route('checkout.account'));
    }

    const parsed = parseDomain(window.location.href);
    this.state.portal = parsed.domain;

    switch (this.state.portal) {
      case 'onlineexpeditors':
      case 'passportcenter':
        this.state.portalNiceName = 'Passport Center';
        break;
      case 'passportrenewal':
        this.state.portalNiceName = 'Passport Renewal';
        break;
      case 'rushmytravelvisa':
        this.state.portalNiceName = 'RushMyTravelVisa';
        break;
      default:
        this.state.portalNiceName = 'Travel Visa';
        break;
    }

    this.ecommerceService.checkout(4, this.cart);

    // If user is logged in, fill the order account info
    if (this.user && this.user.email) {
      this.initReturningUser();
      this.order.account.email = this.user.email;
    }

  }

  initCreditCardName() {

    if (!this.creditCard.name && this.order.account) {
      this.creditCard.name = [
        this.order.account.firstName,
        this.order.account.lastName,
      ].join(' ').trim();
    }
  }

  initReturningUser() {
    const parsed = parseDomain(window.location.href);

    // Account
    const returning = {
      account: {
        firstName: this.user.first_name,
        lastName: this.user.last_name,
        email: this.user.email,
        phone: this.user.phones.length ? this.user.phones[0].value : null,
        country: this.user.phones.length ? this.user.phones[0].country : null,
      },
      billingAddress: this.user.addresses.find(addr => addr.type === 'billing'),
      shippingAddress: this.user.addresses.find(addr => addr.type === 'shipping'),
      creditCard: {
        brand: null,
        last4: null,
      },
    };

    // Credit card
    if (this.user.cards && this.user.cards.length) {
      const card = this.user.cards[this.user.cards.length - 1];
      returning.creditCard.brand = card.brand,
      returning.creditCard.last4 = card.last4;
    }

    this.state.returning = returning;
  }

  /**
   * Get Terms of Use link URL
   */
  determineTermsLink() {
    return this.checkoutService.determineTermsLink();
  }

  /**
   * Get Privacy Policy link URL
   */
  determinePrivacyLink() {
    return this.checkoutService.determinePrivacyLink();
  }

  /**
   * Display total amount
   * @return string
   */
  getOrderTotal() {

    return this.$filter('currency')(this.cartService.getOrderTotal());
  }

  /**
   * Sets nested inner forms as submitted on parent submit
   * @param form
   */
  setSubmitted(form) {
    form.$setSubmitted();

    angular.forEach(form, (innerForm) => {
      if (innerForm && innerForm.$$parentForm === form && innerForm.$setSubmitted) {
        this.setSubmitted(innerForm);
      }
    });
  }


  /**
   * Submit Payment
   */
  async submitPayment() {
    this.setSubmitted(this.form);

    // Is form valid?
    if (this.form.$invalid) {
      return;
    }

    // Check that the user has agreed with the terms
    if (!this.state.terms) {
      this.state.mustAgree = true;
      return;
    }

    this.state.submitting = true;

    let nonce;
    let payload;
    let merchant;
    let processedOrder;

    // ETOMS'API doesn't handle credit card token
    if (!this.etomsService.enabled()) {
      // Set merchant token
      if (!this.user || (this.user && this.user.cards.length === 0)) {
        // Get merchant token
        try {
          const parseUrl = parseDomain(window.location.href);
          const response = await this.orderService.getMerchantToken(parseUrl.domain);
          merchant = response.data;
        } catch (e) {
          this.$log.error('Merchant token failed', e);
          this.state.submitting = false;
          this.bugsnagService.notify(e);
          return this.dialogService.openAlert({});
        }
      } else {
        merchant = this.user.cards[this.user.cards.length - 1];
      }

      // Create payment nonce
      try {
        nonce = await this.authNetService.getNonce(this.creditCard, merchant);
      } catch (e) {
        this.$log.error('Nonce failed', e);
        this.state.submitting = false;
        this.bugsnagService.notify(e);

        // If code is for an invalid credit card, use the payment error dialog
        // handler and 'fake' an error
        const code = Array.isArray(e) && e[0] ? e[0].code : null;
        if (code === 'E_WC_05') {
          const content = this.checkoutService.paymentErrorHandler({ status_code: 6 });
          return this.dialogService.openAlert(content);
        }

        return;
      }
    } else {
      merchant = 'etoms';
      nonce = this.creditCard;
    }

    // Create new order payload
    try {
      payload = this.checkoutService.getNewOrderPayload(this.cart, this.order, merchant, nonce);
    } catch (e) {
      this.$log.error('Order payload failed', e);
      this.state.submitting = false;
      this.bugsnagService.notify(e);
      return this.dialogService.openAlert({});
    }

    // Submit to new order endpoint
    try {
      const response: any = await this.orderService.orderCreate(payload);
      processedOrder = response.data;
    } catch (e) {
      this.$log.error('New order failed', e);
      this.state.submitting = false;

      // Session has expired, so kick user back to Step 2
      if (e.status_code === 403) {
        await this.dialogService.openAlert({
          title: 'Your session has expired',
          text: 'Seems your session has expired. Please login again.',
        });

        return window.location.assign(this.laroute.route('checkout.account'));
      }

      const content = this.checkoutService.paymentErrorHandler(e);
      return this.dialogService.openAlert(content);
    }

    // Send event to Facebook Pixel
    if (typeof this.fbq === 'function') {
      this.fbq('track', 'AddPaymentInfo');
    }

    // Save auth decoration to session so user is logged-in on next page
    if (processedOrder.decoration) {
      try {
        await this.authService.saveDecoration(processedOrder.decoration);
      } catch (e) {
        this.$log.error('Failed to save decoration', e);
        this.state.submitting = false;
        return this.dialogService.openAlert({});
      }
    }

    // Save order to service so we can access it again on next route
    processedOrder.id = 'processed-order';

    try {
      await this.sessionService.saveState(processedOrder);
      return window.location.assign(this.laroute.route('checkout.confirmation'));
    } catch (e) {
      this.$log.error('Failed to save order to session and redirect', e);
      this.state.submitting = false;
      return this.dialogService.openAlert({});
    }
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/components/checkout/submit/submit.component.ts